import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import "./FooterChatbot.css";

type ChatMessage = {
	role: "user" | "assistant";
	content: string;
	countsAsQuestion?: boolean; // Only set for user messages
};

type ChatResponse = {
	answer: string;
	countsAsQuestion?: boolean;
};

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

const FooterChatbot: React.FC<Props> = ({ isOpen, onClose }) => {
	const [isMounted, setIsMounted] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [questionCount, setQuestionCount] = useState(0);

	const [messages, setMessages] = useState<ChatMessage[]>([
		{
			role: "assistant",
			content:
				"Hi, I’m RelBot — your friendly portfolio genie. You get 3 questions. Ask me anything about Lebo (work, projects, preferences, even fun personal curiosities). If it’s not covered by the portfolio knowledge base, I’ll tell you I don’t know — no guessing.",
		},
	]);
	const [question, setQuestion] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const dialogRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const previouslyFocusedRef = useRef<HTMLElement | null>(null);

	const listRef = useRef<HTMLDivElement | null>(null);

	const apiUrl = useMemo(() => {
		return (
			process.env.REACT_APP_AI_CHAT_API_URL ||
			"https://portfolio-chat.princefana7.workers.dev/api/chat"
		);
	}, []);

	// Load question count from localStorage on mount
	useEffect(() => {
		const stored = localStorage.getItem("chatbot_usage");
		if (stored) {
			try {
				const data = JSON.parse(stored);
				const now = Date.now();
				const expiryTime = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
				
				if (data.timestamp && now - data.timestamp < expiryTime) {
					// Not expired yet
					setQuestionCount(data.count || 0);
				} else {
					// Expired, clear it
					localStorage.removeItem("chatbot_usage");
					setQuestionCount(0);
				}
			} catch {
				localStorage.removeItem("chatbot_usage");
				setQuestionCount(0);
			}
		}
	}, []);

	// Save question count to localStorage whenever it changes
	useEffect(() => {
		if (questionCount > 0) {
			localStorage.setItem(
				"chatbot_usage",
				JSON.stringify({
					count: questionCount,
					timestamp: Date.now(),
				})
			);
		}
	}, [questionCount]);

	useEffect(() => {
		if (isOpen) {
			previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
			setIsMounted(true);
			const raf = requestAnimationFrame(() => setIsVisible(true));
			return () => cancelAnimationFrame(raf);
		}

		setIsVisible(false);
		const t = window.setTimeout(() => setIsMounted(false), 240);
		return () => window.clearTimeout(t);
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) return;
		previouslyFocusedRef.current?.focus?.();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const raf = requestAnimationFrame(() => inputRef.current?.focus());
		return () => cancelAnimationFrame(raf);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Tab") {
				const dialog = dialogRef.current;
				if (!dialog) return;
				const active = document.activeElement;
				if (!active || !dialog.contains(active)) return;

				const focusable = Array.from(
					dialog.querySelectorAll<HTMLElement>(
						"button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])"
					)
				).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);

				if (focusable.length === 0) return;
				const first = focusable[0];
				const last = focusable[focusable.length - 1];
				const isShift = e.shiftKey;

				if (!isShift && active === last) {
					e.preventDefault();
					first.focus();
					return;
				}
				if (isShift && active === first) {
					e.preventDefault();
					last.focus();
					return;
				}
			}

			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [isOpen, onClose]);

	useEffect(() => {
		if (!isOpen) return;
		const el = listRef.current;
		if (!el) return;
		el.scrollTop = el.scrollHeight;
	}, [isOpen, messages, isLoading, error]);

	const questionsLeft = Math.max(0, 3 - questionCount);
	const hasQuestionsLeft = questionsLeft > 0;
	const canSend = question.trim().length > 0 && !isLoading && hasQuestionsLeft;

	const send = async () => {
		const trimmed = question.trim();
		if (!trimmed || isLoading) return;

		if (!hasQuestionsLeft) {
			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content:
						"Question limit reached — that's 3 questions for this session. Your limit will reset in 12 hours, or you can email Lebo directly if you want to chat further.",
				},
			]);
			return;
		}

		setError(null);
		setQuestion("");
		setIsLoading(true);

		// Add user message
		setMessages((prev) => [...prev, { role: "user", content: trimmed }]);

		try {
			const res = await fetch(apiUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ question: trimmed }),
			});

			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || `Request failed: ${res.status}`);
			}

			const data = (await res.json()) as ChatResponse;
			
			// Only increment count if this was an actual question (not greeting/clarification)
			if (data.countsAsQuestion !== false) {
				setQuestionCount((prev) => prev + 1);
			}

			setMessages((prev) => [...prev, { role: "assistant", content: data.answer }]);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Unknown error";
			setError(message);
			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content:
						"Sorry — I could not reach the chat service right now. If you want, email me and I’ll respond directly.",
				},
			]);
		} finally {
			setIsLoading(false);
		}
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await send();
	};

	if (!isMounted) return null;

	return (
		<div
			ref={dialogRef}
			className={isVisible ? "footer-chatbot is-open" : "footer-chatbot"}
			role="dialog"
			aria-modal="true"
			aria-labelledby="footer-chatbot-title"
			aria-hidden={!isVisible}
		>
			<div className="footer-chatbot-header">
				<div className="footer-chatbot-title" id="footer-chatbot-title">
					Chat with AI
				</div>
				<button className="footer-chatbot-close" type="button" onClick={onClose} aria-label="Close chat">
					<X size={18} />
				</button>
			</div>

			<div
				className="footer-chatbot-body"
				ref={listRef}
				aria-live="polite"
				aria-relevant="additions"
				aria-busy={isLoading}
			>
				{messages.map((m, idx) => (
					<div key={idx} className={m.role === "user" ? "msg user" : "msg assistant"}>
						{m.content}
					</div>
				))}
				{isLoading ? <div className="msg assistant">Thinking…</div> : null}
				{error ? <div className="footer-chatbot-error">{error}</div> : null}
			</div>

			<form className="footer-chatbot-input" onSubmit={onSubmit}>
				<input
					ref={inputRef}
					className="footer-chatbot-text"
					value={question}
					onChange={(e) => setQuestion(e.target.value)}
					placeholder={hasQuestionsLeft ? `Ask a question… (${questionsLeft} left)` : "No questions left"}
					aria-label="Message"
					disabled={!hasQuestionsLeft}
				/>
				<button className="footer-chatbot-send" type="submit" disabled={!canSend}>
					Send
				</button>
			</form>
		</div>
	);
};

export default FooterChatbot;
